import { useCallback, useEffect, useRef, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { zaiUtils } from 'modules/zai/common/zai.utils'
import { FeatureFlagsStore } from 'stores'
import { PATHNAMES } from 'utils/routes/consts'

import type {
  TZaiSideChatIframeModelReturn,
  TZaiSideChatIframeModelProps,
  TZaiSideChatIframeViewRef,
} from './zai-side-chat-iframe.types'

export const useZaiSideChatIframeModel = ({
  workspaceId,
  language,
}: TZaiSideChatIframeModelProps): TZaiSideChatIframeModelReturn => {
  const location = useLocation()

  const [iframeState, setIframeState] = useState<{ isOpen: boolean; isAnimating: boolean }>({
    isOpen: false,
    isAnimating: false,
  })

  const iframeRef = useRef<TZaiSideChatIframeViewRef>(null)

  const isZaiPage = matchPath(location.pathname, { path: PATHNAMES.WORKSPACE_ZAI, exact: true })
  const isSOAV3WithZaiEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isSOAV3WithZaiEnabled')
  const isSOAPage = matchPath(location.pathname, { path: PATHNAMES.WORKSPACE_SOA, exact: true })

  const shouldRender = !(isSOAPage && isSOAV3WithZaiEnabled)

  const iframeUrlRedirect = `${window.location.origin}/workspace/${workspaceId}/zai`
  const iframeSrc = zaiUtils.generateZaiChatSrc({
    workspaceId,
    module: 'side-chat',
    language,
    redirectUrl: iframeUrlRedirect,
  })

  const handlePostMessage = useCallback(
    (event: MessageEvent) => {
      const eventOriginObjectUrl = new URL(event.origin)
      const eventOrigin = eventOriginObjectUrl.origin

      const iframeOriginObjectUrl = new URL(iframeSrc)
      const iframeOrigin = iframeOriginObjectUrl.origin

      if (eventOrigin !== iframeOrigin || event.data?.action !== 'side-chat' || event.data?.origin !== 'zai-chat')
        return

      setIframeState({ isOpen: !!event.data?.sideChat?.isOpen, isAnimating: !!event.data?.sideChat?.isAnimating })
    },
    [iframeSrc]
  )

  useEffect(() => {
    if (!shouldRender) return
    window.removeEventListener('message', handlePostMessage)

    window.addEventListener('message', handlePostMessage)

    return () => window.removeEventListener('message', handlePostMessage)
  }, [handlePostMessage, shouldRender])

  const onOpenChange = useCallback((isOpen: boolean) => {
    setIframeState({ isOpen, isAnimating: false })
  }, [])

  return {
    ref: iframeRef,
    iframeSrc,
    shouldRender,
    iframeDisplay: (iframeState.isOpen || iframeState.isAnimating) && !isZaiPage,
    isOpen: iframeState.isOpen,
    onOpenChange,
    redirectUrl: iframeUrlRedirect,
  }
}
